/**
 * User-management state that is not part of the Discord-shaped data store:
 * who you ignore, message requests, a pending account recovery and the
 * security alerts the owner and Admins get. `applyUm` is a pure reducer fed
 * by the gateway (store/session.ts), so tests drive it with plain frames.
 * Everything here comes from the server; nothing is guessed.
 */
import {
  snowflakeDate,
  type Channel,
  type GatewayDispatch,
  type IsoDateTime,
  type Message,
  type NxGuildSecurityAlertData,
  type RecoveryRequestInfo,
  type Snowflake,
  type User,
} from '@neosocial/protocol';
import { create } from 'zustand';

/**
 * One message request as the Requests page shows it. The server sends the
 * DM (`GET /users/@me/message-requests`, READY's `nx_message_requests`,
 * `NX_MESSAGE_REQUEST_UPDATE`); the preview is its newest message, loaded
 * with `GET /channels/{id}/messages?limit=1` or taken from MESSAGE_CREATE.
 */
export interface MessageRequest {
  channel: Channel;
  /** Who wrote (the DM's other recipient). */
  user: User;
  /** Null until loaded, or when the server had none. */
  last_message: Message | null;
  created_at: IsoDateTime;
}

/** A view of a pending request DM, or null when it names nobody. */
export function requestFromChannel(
  ch: Channel,
  last: Message | null = null,
): MessageRequest | null {
  const user = ch.recipients?.[0];
  if (!user) return null;
  return {
    channel: ch,
    user,
    last_message: last,
    created_at: last?.timestamp ?? snowflakeDate(ch.last_message_id ?? ch.id).toISOString(),
  };
}

function requestsFrom(list: Channel[]): MessageRequest[] {
  return list.map((c) => requestFromChannel(c)).filter((r): r is MessageRequest => !!r);
}

export interface UmState {
  /** userId -> true for everyone you ignore. */
  ignored: Record<Snowflake, true>;
  /** True once the server told us (READY's nx_ignored_user_ids or GET /users/@me/ignores). */
  ignoresKnown: boolean;
  /** The inbox; null until loaded. */
  requests: MessageRequest[] | null;
  /** Pending requests (the badge). */
  requestCount: number;
  /** A recovery of this account waiting for a hold or an approval. */
  recovery: RecoveryRequestInfo | null;
  /** Newest last; at most 20. */
  alerts: NxGuildSecurityAlertData[];
  /** messageId -> true: a hidden message from someone you ignore, shown on request. */
  revealed: Record<Snowflake, true>;
}

export const emptyUm = (): UmState => ({
  ignored: {},
  ignoresKnown: false,
  requests: null,
  requestCount: 0,
  recovery: null,
  alerts: [],
  revealed: {},
});

function withoutKey<T>(o: Record<string, T>, k: string): Record<string, T> {
  if (!(k in o)) return o;
  const next = { ...o };
  delete next[k];
  return next;
}

/** Applies one dispatch. Unrelated events return the same object. */
export function applyUm(s: UmState, frame: GatewayDispatch): UmState {
  switch (frame.t) {
    case 'READY': {
      const d = frame.d;
      const ignored: Record<Snowflake, true> = {};
      for (const id of d.nx_ignored_user_ids ?? []) ignored[id] = true;
      const requests = d.nx_message_requests ? requestsFrom(d.nx_message_requests) : null;
      return {
        ...emptyUm(),
        ignored,
        ignoresKnown: d.nx_ignored_user_ids !== undefined,
        requests,
        requestCount: requests?.length ?? 0,
        // READY has no recovery: GET /auth/recovery/pending fills it (session.ts)
        recovery: s.recovery,
      };
    }
    case 'NX_IGNORE_ADD':
      if (s.ignored[frame.d.user_id]) return s;
      return { ...s, ignored: { ...s.ignored, [frame.d.user_id]: true } };
    case 'NX_IGNORE_REMOVE':
      if (!s.ignored[frame.d.user_id]) return s;
      return { ...s, ignored: withoutKey(s.ignored, frame.d.user_id) };
    case 'NX_MESSAGE_REQUEST_UPDATE': {
      const d = frame.d;
      const list = s.requests;
      const had = list?.some((r) => r.channel.id === d.channel_id) ?? false;
      if (d.request_state === 'pending') {
        const view = d.channel ? requestFromChannel(d.channel) : null;
        if (!view) return { ...s, requestCount: had ? s.requestCount : s.requestCount + 1 };
        const next = list ? [view, ...list.filter((r) => r.channel.id !== d.channel_id)] : list;
        return { ...s, requests: next, requestCount: had ? s.requestCount : s.requestCount + 1 };
      }
      // accepted or dismissed: it leaves the inbox
      return {
        ...s,
        requests: list ? list.filter((r) => r.channel.id !== d.channel_id) : list,
        requestCount: Math.max(0, s.requestCount - (had || !list ? 1 : 0)),
      };
    }
    case 'MESSAGE_CREATE': {
      // the newest message of a request becomes its preview
      const m = frame.d;
      const list = s.requests;
      if (!list?.some((r) => r.channel.id === m.channel_id)) return s;
      return {
        ...s,
        requests: list.map((r) =>
          r.channel.id === m.channel_id ? { ...r, last_message: m, created_at: m.timestamp } : r,
        ),
      };
    }
    case 'NX_RECOVERY_PENDING': {
      const d = frame.d;
      const live = d.state === 'pending' || d.state === 'approved';
      if (live) return { ...s, recovery: d };
      return s.recovery?.id === d.id ? { ...s, recovery: null } : s;
    }
    case 'NX_GUILD_SECURITY_ALERT':
      return { ...s, alerts: [...s.alerts, frame.d].slice(-20) };
    default:
      return s;
  }
}

export const useUm = create<UmState>(() => emptyUm());

export function feedUm(frame: GatewayDispatch): void {
  const s = useUm.getState();
  const next = applyUm(s, frame);
  if (next !== s) useUm.setState(next, true);
}

export function resetUm(): void {
  useUm.setState(emptyUm(), true);
}

export function isIgnored(userId: Snowflake | undefined | null): boolean {
  return !!userId && !!useUm.getState().ignored[userId];
}

export function setIgnoredList(ids: Snowflake[]): void {
  const ignored: Record<Snowflake, true> = {};
  for (const id of ids) ignored[id] = true;
  useUm.setState({ ignored, ignoresKnown: true });
}

export function setRequests(list: MessageRequest[]): void {
  useUm.setState({ requests: list, requestCount: list.length });
}

/** The newest open recovery of this account (GET /auth/recovery/pending), or none. */
export function setRecovery(list: RecoveryRequestInfo[]): void {
  const open = list.filter((r) => r.state === 'pending' || r.state === 'approved');
  useUm.setState({ recovery: open[open.length - 1] ?? null });
}

export function revealMessage(id: Snowflake, on: boolean): void {
  const s = useUm.getState();
  useUm.setState({ revealed: on ? { ...s.revealed, [id]: true } : withoutKey(s.revealed, id) });
}
